import { useState } from "react";

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageEl = form.querySelector<HTMLTextAreaElement>("textarea[name='message']");
    if (messageEl && messageEl.value.length > 500) {
      return;
    }
    setFormStatus("sending");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d77pp7idqn9mib2tqbng", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section
      id="contact"
      className="w-full py-24 md:py-36"
      style={{ background: "linear-gradient(180deg, #f8f9fa 0%, #f0f2f5 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-16">
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">Get In Touch</p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact Us
            <span className="block text-gray-400 font-light">We&apos;d Love to Hear From You</span>
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Contact Info */}
          <div className="lg:w-2/5 space-y-8">
            {/* Company info card */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://public.readdy.ai/ai/img_res/e61cd23f-fa0e-48b0-85ee-c610c564b211.png"
                  alt="CUB SRL Logo"
                  className="h-10 w-auto object-contain"
                />
                <span
                  className="text-xl font-bold text-gray-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  CUB SRL
                </span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-6">
                A team of iOS experts dedicated to building secure, high-performance mobile applications that users love.
              </p>

              <div className="space-y-4">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-gray-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Address</p>
                    <p className="text-sm text-gray-700 font-medium">COM. MARGINENI Nr 8 Etaj P</p>
                    <p className="text-sm text-gray-500">City Margineni, Zip 507093</p>
                    <p className="text-sm text-gray-500">Romania</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-gray-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Email</p>
                    <p className="text-sm text-gray-700 font-medium">help@cubsrl.com</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-gray-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Phone Number</p>
                    <p className="text-sm text-gray-700 font-medium">+1 6812666813</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative image */}
            <div className="rounded-2xl overflow-hidden h-48">
              <img
                src="https://public.readdy.ai/ai/img_res/57184d41e584a41c5c30824c2bde76d9.jpg"
                alt="Romania - CUB SRL location"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-3/5">
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Send Us a Message</h3>

              {formStatus === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mb-4">
                    <i className="ri-check-line text-emerald-600 text-2xl" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Message Sent!</h4>
                  <p className="text-sm text-gray-500">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-6 px-6 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gray-700 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="cubsrl-contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors bg-gray-50"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors bg-gray-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="How can we help?"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us more about your inquiry..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors bg-gray-50 resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? "text-red-400" : "text-gray-400"}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {formStatus === "error" && (
                    <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "sending" || charCount > 500}
                    className="w-full py-3.5 rounded-lg bg-gray-900 text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === "sending" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="mt-20 border-t border-gray-200 pt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} <strong>CUB SRL</strong>. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-map-pin-2-line text-gray-400" />
            <span>COM. MARGINENI Nr 8 Etaj P, City Margineni, Zip 507093, Romania</span>
          </div>
        </div>
      </div>
    </section>
  );
}
